import { ReportType } from '@/plugins/kmon'
import debounce from 'lodash/debounce'

type MediaType = 'video' | 'audio';
type RecordType = 'start' | 'end';

class DwManager {
  private performanceData = {
    total_time: 0,
    buffer_time: 0,
    first_frame_time: 0,
    load_time: 0
  };
  private playStatus = {
    play_success: true,
    play_error: ''
  };
  private convertStatus = {
    convert_success: true,
    convert_error: ''
  };
  private requestData = {
    is_success: true,
    time: 0,
    type: ''
  };
  private initialBufferingRecorded = false;
  private initialFileLoaded = false;
  private firstEntry = true;
  private lastPlayTime = 0;
  private totalPlayTime = 0;
  private isPlaying = false;

  constructor(
    private report: (type: ReportType, value: any) => void
  ) {}

  public reportShowAction(fileType: MediaType, options: any = {}): void {
    const { mediaInfo, fileInfo } = options
    this.report('show', {
      fileType,
      mediaInfo,
      fileInfo
    })
  }

  public reportPlayAction(fileInfo: any): void {
    this.isPlaying && this.recordPlayTime('ended')
    this.report('play', {
      fileInfo,
      play_time: (this.totalPlayTime / 1000).toFixed(2),
      ...this.playStatus,
      ...this.convertStatus,
      ...this.performanceData
    })
  }

  public reportMediaApiAction(params: object): void {
    this.report('media_api', {
      ...this.requestData,
      ...params
    })
  }

  public reportUserActiveAction = debounce(
    (action: string, fileType: string): void => {
    
      if (this.firstEntry && action === 'volumechange') {
        this.firstEntry = false
        return
      }
      this.report('user_active', {
        action,
        type: fileType
      })
    },
    500,
    { leading: true, trailing: false }
  );

  public measureFirstTimeBuffering(record: RecordType): void {
    const func = {
      start: () => {
        if (!this.initialBufferingRecorded) {
          performance.mark('bufferingStart')
        }
      },
      end: () => {
        if (
          !this.initialBufferingRecorded &&
          performance.getEntriesByName('bufferingStart').length
        ) {
          performance.mark('bufferingEnd')
          performance.measure(
            'Initial Buffering Time',
            'bufferingStart',
            'bufferingEnd'
          )
          const bufferingDuration = performance.getEntriesByName(
            'Initial Buffering Time'
          )[0].duration
          this.performanceData.buffer_time = +bufferingDuration.toFixed(2)
          this.initialBufferingRecorded = true
          this.resetMarks(
            ['bufferingStart', 'bufferingEnd'],
            ['Initial Buffering Time']
          )
        }
      }
    }

    try {
      func[record]()
    } catch (error) {
      console.error('Error in measuring first time buffering:', error)
    }
  }

  public measureFirstFrameLoaded(record: RecordType): void {
    const func = {
      start: () => {
        performance.mark('loadFirstFrameStart')
      },
      end: () => {
        performance.mark('loadFirstFrameEnd')
        performance.measure(
          'loadedFirstFrame',
          'loadFirstFrameStart',
          'loadFirstFrameEnd'
        )
        const loadedDuration = performance.getEntriesByName(
          'loadedFirstFrame'
        )[0].duration
        this.performanceData.first_frame_time = +loadedDuration.toFixed(2)
        this.resetMarks(
          ['loadFirstFrameStart', 'loadFirstFrameEnd'],
          ['loadedFirstFrame']
        )
      }
    }

    try {
      func[record]()
    } catch (error) {
      console.error('Error in measuring first frame loaded:', error)
    }
  }

  public measureFileLoaded(record: RecordType): void {
    const func = {
      start: () => {
        if (!this.initialFileLoaded) {
          performance.mark('fileLoadStart')
        }
      },
      end: () => {
        if (!this.initialFileLoaded) {
          performance.mark('fileLoadEnd')
          performance.measure('loadedFile', 'fileLoadStart', 'fileLoadEnd')
          const loadedDuration = performance.getEntriesByName('loadedFile')[0].duration
          this.performanceData.load_time = +loadedDuration.toFixed(2)
          this.resetMarks(['fileLoadStart', 'fileLoadEnd'], ['loadedFile'])
          this.initialFileLoaded = true
        }
      }
    }

    try {
      func[record]()
    } catch (error) {
      console.error('Error in measuring file loaded:', error)
    }
  }

  public measureRequestLoadTime(record: RecordType): void {
    const func = {
      start: () => {
        performance.mark('apiRequestStart')
      },
      end: () => {
        performance.mark('apiRequestEnd')
        performance.measure('requestDuration', 'apiRequestStart', 'apiRequestEnd')
        const loadedDuration = performance.getEntriesByName('requestDuration')[0].duration
        this.requestData.time = +loadedDuration.toFixed(2)
        this.resetMarks(['apiRequestStart', 'apiRequestEnd'], ['requestDuration'])
      }
    }

    try {
      func[record]()
    } catch (error) {
      console.error(error)
    }
  }

  recordInitialPlayStatus(code) {
    this.playStatus.play_error = code ? this.getErrorMessage(code) : ''
    this.playStatus.play_success = !code
  }
  recordTotalTime(totalTime) {
    this.performanceData.total_time = +totalTime.toFixed(2)
  }
  recordPlayTime(event) {
    const func = {
      play: () => {
        this.lastPlayTime = performance.now()
        this.isPlaying = true
      },
      pause: () => {
        this.totalPlayTime += performance.now() - this.lastPlayTime
        this.isPlaying = false
      },
      ended: () => {
        this.totalPlayTime += performance.now() - this.lastPlayTime
        this.isPlaying = false
      }
    }

    try {
      func[event]()
    } catch (error) {
      console.error(error)
    }
  }
  recordConvertStatus(msg) {
    this.convertStatus.convert_error = msg || ''
    this.convertStatus.convert_success = !msg
  }

  private getErrorMessage(code: number): string {
    const errorMessages = {
      1: 'MEDIA_ERR_ABORTED: The fetching process was aborted by the user.',
      2: 'MEDIA_ERR_NETWORK: A network error prevented the media from being downloaded.',
      3: 'MEDIA_ERR_DECODE: An error occurred while decoding the media.',
      4: 'MEDIA_ERR_SRC_NOT_SUPPORTED: The media format is not supported or the source is missing.',
      default: 'Unknown error'
    }

    return errorMessages[code] || errorMessages.default
  }

  
  private resetMarks(marks: string[], measures: string[]): void {
    marks.forEach(mark => {
      performance.clearMarks(mark)
    })
    measures.forEach(measure => {
      performance.clearMeasures(measure)
    })
  }
}

export default DwManager
